<template>
  <div id="map" style="z-index: 1;" v-resize="onResize">
    <div v-if="hasWatermark" id="watermark" class="pl-15 pr-15">
      <div v-if="watermark.type === 'image'">
        <img class="pa-16" v-for="i in 100"
             style="pointer-events: none;" :src="watermark.content"
             />
      </div>
      <div v-if="watermark.type === 'text'">
        <div class="pa-16 float-left watermark-text-rotated" v-for="i in 100" style="pointer-events: none;"><span
          v-for="(line, i) in watermark.content">{{line }}<br/></span></div>
      </div>
    </div>
    <popup-info ref="popup" v-if="CONFIG.config.infoPopup" @close="handlePopupClose"></popup-info>
    <mapbox-attribution v-if="isMapbox"/>
  </div>
</template>

<script>
import 'gox';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { mapGetters, mapState } from 'vuex';
import getFeatureInfo from '../../lib/getFeatureInfo';
import MapboxAttribution from './MapboxAttribution';
import PopupInfo from '@/components/Map/PopupInfo';
import Vue from 'vue';
import { GeoJSON } from 'ol/format';
import { Overlay } from 'ol';
import utils from '@/utils';
import * as url from 'url';


const defs = [
  [
    'EPSG:27700',
    '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs',
  ],
  [
    'EPSG:32630',
    '+proj=utm +zone=30 +ellps=WGS84 +datum=WGS84 +units=m +no_defs',
  ],
  [
    'EPSG:29902',
    '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000 +ellps=mod_airy +towgs84=482.5,-130.6,564.6,-1.042,-0.214,-0.631,8.15 +units=m +no_defs',
  ],
  ['EPSG:32619', '+proj=utm +zone=19 +datum=WGS84 +units=m +no_defs'],
  [
    'EPSG:3844',
    '+proj=sterea +lat_0=46 +lon_0=25 +k=0.99975 +x_0=500000 +y_0=500000 +ellps=krass +towgs84=2.329,-147.0416,-92.0802,-0.30924979,0.32482188,0.49730012,5.68907711 +units=m +no_defs',
  ],
  ['EPSG:32634', '+proj=utm +zone=34 +datum=WGS84 +units=m +no_defs'],
  [
    'EPSG:32616',
    '+proj=utm +zone=16 +ellps=WGS84 +datum=WGS84 +units=m +no_defs',
  ],
];

// This sets the projdefs for GOX
GOX.setProjDefs(defs);

// And now set the same ones for any other usages of proj4
defs.forEach((def) => {
  proj4.defs(def[0], def[1]);
});
register(proj4);

export default {
  name: 'TheMap',
  components: {
    PopupInfo,
    MapboxAttribution
  },
  data() {
    return {
      map: null,
      showPopup: false,
      overlay: null,
      popupHtml: null
    };
  },


  computed: {
    ...mapGetters({
      CONFIG: 'CONFIG',
      TOOLS: 'TOOLS',
      COLLECTIONS: 'layers/COLLECTIONS',
      LAYERS: 'layers/LAYERS',
      MASK: 'GET_ORG_MASK',
      CHECK_SYSTEM_TYPE: 'CHECK_SYSTEM_TYPE'
    }),
    ...mapState('featureInfo', {
      RESULTS: 'results'
    }),
    ...mapState('layers', ['active']),
    hasWatermark() {
      let wm = this.CONFIG.config.watermark;
      return !!(wm && wm.content);
    },
    watermark() {
      let type = 'text';

      let wm = this.CONFIG.config?.watermark?.content;

      if (wm.indexOf('https://') === 0) {
        //try to load an image
        type = 'image';
      } else {
        wm = wm.split('\\n');
      }

      return {
        type,
        content: wm
      };
    },
    isMapbox() {
      if (this.map) {
        let isMapbox = false;
        this.$MAP.getLayers()
          .getArray()
          .forEach(layer => {
            if (layer.type === 'TILE') {
              if (layer.getSource()
                .getUrls()
                .join()
                .indexOf('mapbox') > -1) {
                isMapbox = true;
              }
            }
          });

        return isMapbox;
      } else {
        return false;
      }
    }
  },

  methods: {
    addEvents() {
      if (this.TOOLS.indexOf('query') > -1) {
        console.log('ENABLE QRY');
        this.query = new getFeatureInfo(this.map);
      }
    },
    addStartLayers() {
      const startLayers = this.CONFIG.config.startLayers;
      console.dir(startLayers);
      if (this.CONFIG.type === 'layers_only') {
        startLayers.forEach(layer => {
          console.log(layer);
          this.$store.dispatch('layers/ADD_LAYER', {
            layer: this.LAYERS[layer],
            checkWatermark: false,
          });
        });
        return;
      }
      this.COLLECTIONS.forEach((col, colIdx) => {
        col.layers.forEach((lay, layIdx) => {
          const fullLayerID = `${col.id}:${lay}`;
          if (!startLayers || startLayers.indexOf(fullLayerID) > -1) {
            const theLayer = this.LAYERS[lay];
            // Only add a watermark to the last one
            this.$store.dispatch('layers/ADD_LAYER', {
              layer: theLayer,
              checkWatermark: false,
            });
          }
        });
      });

      this.updateLayerState();

    },
    createMask() {
      let mask = this.MASK;
      if (mask && this.CHECK_SYSTEM_TYPE('parishonline')) {
        let gj = new GeoJSON();
        const feature = gj.read(mask);
        this.mapMask = new GOX.Layer.MapMask(feature, {
          fillColor: '#FFFFFF',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWidth: 2
        });
        this.$MAP.maskOn = true;
      }
    },
    checkUrlStartParams() {
      let searchParams = utils.getURLParams(window.location.href);

      if (searchParams.x && searchParams.y) {
        this.CONFIG.config.center = [Number(searchParams.x), Number(searchParams.y)];
        this.CONFIG.config.zoom = searchParams.z || 12;
        return true;
      }
      return false;
    },
    checkLabel() {
      let searchParams = utils.getURLParams(window.location.href);
      if (searchParams.label) {
        let marker = new GOX.Marker(this.map, [searchParams.x, searchParams.y], 'pmv-marker', {
          moveable: false,
          clickable: true,
          style: {
            fill: {
              color: 'red',
            },
            text: {
              value: searchParams.label,
              alignment: 'right',
              offsetX: 12,
              size: 14,
              color: 'black',
              stroke: {
                color: 'white',
                width: 3,
              },
            },
            symbol: {
              size: 8,
              fill: 'red',
            },
            stroke: {
              color: 'white',
            },
          },
        });
      }
    },
    addOverlay() {
      if (this.CONFIG.config.watermark) {
        let overlay;
        console.log('ADD WATERMARK');

        let overlays = [
          {
            position: () => {

              let extent = this.map.getView()
                .calculateExtent();
              return [extent[0], extent[3]];
            },
            positioning: 'top-left'
          },
        ];

        overlays.forEach((o, i) => {
          o.overlay = new Overlay({
            id: 'watermark' + i,
            stopEvent: false,
            element: document.getElementById('watermark'),
            position: o.position(),
            positioning: o.positioning
          });

          this.map.addOverlay(o.overlay);
        });

        this.map.on('postcompose', () => {

          overlays.forEach(o => {
            o.overlay.setPosition(o.position());
          });
        });
      }
    },
    createMap() {

      let overrideStartExtent = this.checkUrlStartParams();

      this.map = new GOX.Map({
        fullscreen: false,
        permalink: false,
        target: 'map',
        projection: this.CONFIG.config.projection || 'EPSG:3857',
        center: this.CONFIG.config.center && this.CONFIG.config.center.length ? this.CONFIG.config.center : null,
        zoom: this.CONFIG.config.zoom || 12
      });

      let hasLabel = this.checkLabel();

      Vue.prototype.$MAP = this.map;

      this.handleColourChanges();

      if (!overrideStartExtent) {
        if (this.CONFIG.config.extent && !this.CONFIG.config.startExtent) {
          let ext = this.CONFIG.config.extent;
          if (typeof ext === 'string') {
            ext = ext.split(',');
          }
          const newExt = ext.map(c => parseInt(c, 10));
          this.map.setExtent(newExt);
        }

        if (this.CONFIG.config.startExtent) {
          console.log('SET START EXT');
          this.map.setExtent(this.CONFIG.config.startExtent);
        }

        this.addOverlay();

      }

      setTimeout(() => {
        this.$MAP.updateSize();
        this.createTrackingEvents();
      }, 500);

    },
    handlePopupClose() {
      console.log('CLOSE POPUP');
      this.$store.commit('featureInfo/CLEAR_QUERY');

      var closer = document.getElementById('popup-closer');
      this.overlay.setPosition(undefined);
      closer.blur();
      return false;
    },
    handleInfoPopup(evt) {
      if (this.overlay) {
        this.overlay.setPosition(undefined);
      }

      if (!this.RESULTS.length) {
        return;
      }

      var container = document.getElementById('popup');
      console.dir(container);

      /**
       * Create an overlay to anchor the popup to the map.
       */
      this.overlay = new Overlay({
        element: container,
        autoPan: true,
        autoPanAnimation: {
          duration: 250,
        },
      });

      this.map.addOverlay(this.overlay);
      console.dir(evt);
      var coordinate = evt.coordinate;
      this.overlay.setPosition(coordinate);
    },
    createHighlightLayer() {
      let layer = new GOX.Layer.Vector({
        id: 'info-highlight',
      });
      layer.setZIndex(999999999999);

      let inner = new GOX.Style().JSONtoStyle({
        stroke: {
          width: 3,
          color: 'rgb(49,118,255)'
        },
        symbol: {
          radius: 3
        }
      });

      let outer = new GOX.Style().JSONtoStyle({
        stroke: {
          width: 5,
          color: 'rgb(255,255,255)'
        }
      });

      layer.setStyle([outer, inner]);
      this.$MAP.addLayer(layer);
    },
    updateLayerState() {
      this.active.forEach(l => {
        console.log('Update State', l);

        let {
          minscale,
          maxscale
        } = this.LAYERS[l];

        let currentRes = this.map.getView()
          .getResolution();
        let minRes = this.map.getResolutionFromScale(minscale);
        let maxRes = this.map.getResolutionFromScale(maxscale);

        console.log(minscale, maxscale);

        console.log(currentRes, minRes, maxRes);

        let message = null;

        if (currentRes < minRes) message = 'magnify-minus';
        if (currentRes > maxRes) message = 'magnify-plus';

        this.$store.commit('layers/SET_LAYER_STATE', {
          layer: l,
          active: true,
          message
        });
      });

      //this.$store.commit('layers/SET_LAYER_STATE', )
    },
    createTrackingEvents() {
      /**
       * Handles info click event, added intially for popover style but could be used for all info click events to make things tidier
       */
      this.map.on('info-click', e => {
        if (this.CONFIG.config.infoPopup) {
          this.handleInfoPopup(e);
        }
      });

      this.map.on('rendercomplete', (e) => {
        this.updateLayerState();
      });

      this.map.on('moveend', (e) => {

        window.parent.postMessage({
          source: 'gx.publicmap',
          type: 'move-end',
          data: {
            mapMask: null,
            extent: this.map.getExtent(),
            coordinate: this.map.getView()
              .getCenter(),
            epsg: this.CONFIG.config.projection || 'EPSG:3857',
          },
        }, '*');
      });

      this.map.on('singleclick', (e) => {
        window.parent.postMessage({
          source: 'gx.publicmap',
          type: 'map-click',
          data: {
            coordinate: e.coordinate,
            epsg: this.CONFIG.config.projection || 'EPSG:3857',
          },
        }, '*');
      });
    },
    /**
     * Change css for map controls
     */
    handleColourChanges() {
      $('.ol-zoom')
        .css({
          'backgroundColor': this.$vuetify.theme.themes.light['primary'],
          'color': this.appTextColour
        });
      $('.ol-zoom-out')
        .css({
          'backgroundColor': this.$vuetify.theme.themes.light['primary'],
          'color': this.appTextColour
        });
      $('.ol-zoom-in')
        .css({
          'backgroundColor': this.$vuetify.theme.themes.light['primary'],
          'color': this.appTextColour
        });
      $('.ol-attribution')
        .css({ 'backgroundColor': this.$vuetify.theme.themes.light['primary'] });
      $('.ol-attribution img')
        .css('display', 'none !important');
      $('.ol-attribution > button:after')
        .css('content', '© !important');
      $('.ol-attribution ul')
        .css({
          'color': 'white',
          'textShadow': 'none'
        });
      $('.ol-attribution button')
        .css({
          'backgroundColor': this.$vuetify.theme.themes.light['primary'],
          'color': this.appTextColour
        });
    },
    onResize() {
      if (this.$MAP) {
        this.$MAP.updateSize();
      }
    }
  },

  mounted() {
    this.createMap();
    //this.createMask();
    this.createHighlightLayer();
    this.addEvents();
    this.addStartLayers();
  },
};
</script>

<style scoped>
#map {
  height: 100%;
  width: 100%;
  top: 0px;
  left: 0px;
  position: relative;
}

.ol-popup {
  position: absolute;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  border: 1px solid #cccccc;
  bottom: 12px;
  left: -50px;
  min-width: 280px;
}

.ol-popup:after,
.ol-popup:before {
  top: 100%;
  border: solid transparent;
  content: ' ';
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}

.ol-popup:after {
  border-top-color: white;
  border-width: 10px;
  left: 48px;
  margin-left: -10px;
}

.ol-popup:before {
  border-top-color: #cccccc;
  border-width: 11px;
  left: 48px;
  margin-left: -11px;
}

.ol-popup-closer {
  text-decoration: none;
  position: absolute;
  top: 2px;
  right: 8px;
}

.ol-popup-closer:after {
  content: '✖';
}

#watermark {
  width: 150%;
  height: 150%;
  opacity: 0.5;
  top: 0px;
  left: -15%;
  position: relative;
  align-content: center;
  justify-content: center;
  text-decoration: none;
  color: rgba(0, 0, 0, 1);
  font-size: 14pt;
  font-weight: bold;
  text-align: center; /* align center all inline elements */
}

.watermark-text-rotated {
  transform: rotate(-30deg);
}
</style>
